import { useEffect } from "react";
import { motion } from "framer-motion";
import { BarChart3, Clock, Users, CalendarCheck, DollarSign, Bot } from "lucide-react";
import { RevShell, ModuleBar, RevHero, SectionHead, RevCTA, BrowserFrame, StatChip } from "./_shared";

const METRICS = [
  "Leads by source", "Qualified-lead rate", "Time to first response", "Conversations started",
  "Meetings booked", "No-show rate", "Opportunities created", "Pipeline value",
  "Sales closed", "Cost per lead", "Cost per qualified appointment", "Revenue by campaign",
  "AI vs human-assisted results", "By location, language & agent", "Opt-out & bounce rates", "Estimated hours saved",
];

function DashMock() {
  const funnel = [
    { t: "Visitors", v: 4200, w: "100%" },
    { t: "Conversations", v: 980, w: "70%" },
    { t: "Qualified", v: 402, w: "48%" },
    { t: "Meetings booked", v: 156, w: "30%" },
    { t: "Won", v: 41, w: "16%" },
  ];
  return (
    <BrowserFrame url="app.websitetech.pro/revenue/dashboard">
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="rounded-lg bg-blue-50 px-3 py-2"><div className="text-lg font-bold text-brand">$128k</div><div className="text-[10px] text-slate-500">Pipeline</div></div>
        <div className="rounded-lg bg-emerald-50 px-3 py-2"><div className="text-lg font-bold text-emerald-600">41</div><div className="text-[10px] text-slate-500">Won</div></div>
        <div className="rounded-lg bg-slate-100 px-3 py-2"><div className="text-lg font-bold text-slate-700">18s</div><div className="text-[10px] text-slate-500">First reply</div></div>
      </div>
      <div className="space-y-1.5">
        {funnel.map((f) => (
          <div key={f.t} className="flex items-center gap-3">
            <span className="text-[11px] text-slate-500 w-28 shrink-0">{f.t}</span>
            <div className="flex-1 h-6 rounded-md bg-slate-100 overflow-hidden">
              <div className="h-full bg-brand/80 rounded-md flex items-center justify-end pr-2 text-[10px] font-bold text-white" style={{ width: f.w }}>{f.v}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="text-[10px] text-slate-400 mt-3">Illustrative — your real numbers appear once live.</div>
    </BrowserFrame>
  );
}

export default function RevenueDashboard() {
  useEffect(() => { window.scrollTo(0, 0); document.title = "Revenue Intelligence Dashboard | AI Revenue Center"; }, []);
  return (
    <RevShell testid="rev-dashboard-page">
      <RevHero eyebrow="AI Revenue Center™ · Revenue Intelligence"
        title="Beyond page views —" accent="down to revenue."
        sub="Finally connect your website analytics to the money. See which sources, campaigns and agents produce qualified leads, booked meetings and closed sales — and how much time the system saved you."
        visual={<DashMock />} />
      <ModuleBar />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <SectionHead eyebrow="Illustrative snapshot" title="The numbers that actually matter" sub="Example figures — not guarantees. Your real data replaces these on day one." />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          <StatChip label="Cost per qualified appt." value="$14" tone="emerald" />
          <StatChip label="Revenue by campaign" value="$128k" tone="brand" />
          <StatChip label="Hours saved / mo" value="~90" tone="slate" />
          <StatChip label="AI-assisted wins" value="63%" tone="amber" />
        </div>
      </section>

      <section className="py-16 bg-white border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Full metric set" title="Prospect → meeting → revenue attribution" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-8">
            {METRICS.map((m, i) => (
              <motion.div key={m} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.03 }}
                className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"><BarChart3 size={15} className="text-brand shrink-0" /> {m}</motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid sm:grid-cols-3 gap-4">
          {[{ icon: Clock, t: "Speed", d: "Time-to-first-response, by hour and channel." },
            { icon: Users, t: "Quality", d: "Qualified-lead rate by source and campaign." },
            { icon: Bot, t: "AI vs human", d: "See where automation helps — and where people close." }].map((x) => (
            <div key={x.t} className="rounded-2xl border border-slate-200 bg-white card-shadow p-6">
              <div className="h-10 w-10 rounded-xl bg-brand/10 grid place-items-center text-brand"><x.icon size={18} /></div>
              <div className="font-heading font-bold text-slate-900 mt-3">{x.t}</div>
              <p className="text-sm text-slate-500 mt-1.5">{x.d}</p>
            </div>
          ))}
        </div>
      </section>

      <RevCTA title="Prove what's driving revenue." sub="Start with a free audit and we'll wire up attribution from first visit to closed deal." />
    </RevShell>
  );
}
